import { ModuleSupplyStrip } from './ModuleSupplyStrip.tsx'
import type { SuppliesView } from './MonitoringScreen.tsx'
import { useModuleProgress } from '@ctcells/api'
import type { ChamberRow, ModuleSnapshot, TrayCell, VisionFeed } from '@ctcells/contract'
import { visionStreamFor } from '@ctcells/core'
import { moduleLabel, t } from '@ctcells/i18n'
import { useContext, type ReactNode } from 'react'

import { cx } from '../../atoms/cx.ts'
import {
  AssemblerFigure,
  GantryFigure,
  GantryFocusFigure,
  ReagentBayFigure,
  RotorFigure,
  ShakerFigure,
  TcRackFigure,
  TrayFigure,
  VisionCameraButton,
  VisionFrame,
  VisionStream,
  type TrayCellView,
  type VisionCellView,
} from '../../figures/index.ts'
import {
  AssemblerStationLine,
  StainingFrontLine,
  type StainDeckNow,
  type StainFrontPhase,
  type StainWasteKind,
  type StationPhase,
} from '../../figures/LineFigures.tsx'
import type { VisionStreamState } from '../../figures/visionStreamClient.ts'
import { FigureLegend } from '../../layout/FigurePanel.tsx'

import {
  consumableNumber,
  rotorSlots,
  telemetryNumber,
  trayCells,
  TRAY_LABEL_KEY,
  TRAY_SLOTS,
  whereOf,
  type MonitoringDeviceKey,
} from './monitoringModel.ts'
import { RobotArm } from './RobotArm.tsx'
import { RobotLine } from './RobotLine.tsx'

import {
  activityText,
  isActive,
  NO_ACTIVITY,
  RAIL_COMPONENT,
  stepActivity,
  type StepActivity,
} from './stepActivityModel.ts'
import { shakerView } from './shakerModel.ts'
import { stainFrontWell } from './stainFrontModel.ts'
import { temporaryChamberFlow } from './temporaryStepMock.ts'
import {
  StainLatestWellContext,
  TrayViewContext,
  trayViewAssemblerSlot,
  trayNameFor,
  trayViewCells,
  trayViewCount,
  trayViewRotor,
} from './trayViewModel.ts'

import './monitoring.css'

/**
 * 🔴 **콘솔 모니터링과 모듈 패드가 이 파일 하나를 같이 쓴다.** 그림을 두 벌로 만들지 마라 —
 *    갈라 놓으면 반드시 어긋난다(`figures/index.ts` 가 같은 이유로 그림을 따로 뒀다).
 *    모듈마다 다른 것은 **키 하나**이고, 그림 구성은 데이터(`figureTabs` · `TRAY_LABEL_KEY`)가 가른다.
 */
export interface FocusFigureProps {
  deviceKey: MonitoringDeviceKey
  /** 지금 고른 그림 탭 */
  component: string
  snapshot: ModuleSnapshot | undefined
  chambers: readonly ChamberRow[]
  /**
   * 🔴 **없는 것이 live 의 정답이다.** 서버에 시료↔칸 대응이 없다(`ChamberRow` 머리말 · BR-31) —
   *    없으면 칸 이름만 있는 빈 격자를 그리고 **「칸 배정 정보 없음」**을 함께 적는다.
   *    그 문구가 이 화면의 정직함 전부다: 빈 격자만 그리면 「트레이가 비었다」는 거짓이 된다.
   */
  tray?: readonly TrayCell[] | undefined
  running: boolean
  canViewPii: boolean
  visionFeed: VisionFeed | undefined
  /**
   * 「전체」 보기에서 그림을 눌렀을 때. **탭을 누른 것과 완전히 같은 경로**다 —
   * 호출부가 탭과 같은 `onChange` 를 넘겨 상태를 한 벌로 유지한다(§14-3).
   */
  onSelectComponent?: ((id: string) => void) | undefined
  /**
   * 누를 수 있는 부품 키. 🔴 **탭 목록에서 그대로 온다.** 탭이 없는 부품은 누를 수 없다 —
   * 염색기·어셈블러에는 `gantry` 탭이 없어서 레일을 눌러도 갈 곳이 없다(§19-3).
   */
  selectableComponents?: readonly string[] | undefined
  /**
   * 「전체」 보기에 **덧붙일 조각들.** 🔴 **기본은 전부 꺼짐 = 지금 콘솔(시료 관점) 그대로**다.
   * 장비 관점 콘솔만 켠다 — 켜면 v0.1.0 시점 구성이 그대로 돌아온다.
   */
  overview?: OverviewParts | undefined
  /**
   * 트레이 이름표에 **「작업 전 / 작업 후」를 덧붙인다** — 「C 트레이 · 작업 전」.
   *
   * 🔴 **기본은 꺼짐 = 지금 콘솔 장비 탭 말고는 한 글자도 안 바뀐다.** 장비 관점 콘솔 ·
   *    태블릿 · 모듈 패드의 「트레이」 탭은 이 깃발을 안 켜므로 종전 그대로다.
   * 🔴 **두 장이 나란히 설 때만 붙는다**(`trays`). 염색기(`tray`)는 같은 트레이가 슬라이더로
   *    자리만 옮기는 **한 장**이라(§16-2) 「작업 전」을 붙이면 거짓이 된다 — 그 한 장이
   *    작업 후이기도 하다. 붙일 것이 없으니 안 붙인다.
   * ⚠ 이 깃발이 필요해진 까닭: 어느 쪽이 작업 전인지 말해 주던 것이 「전체」 보기 **범례**였는데
   *   콘솔에서 「전체」가 사라지며 같이 없어졌다(eric 지시 2026-09-21).
   */
  trayStage?: boolean | undefined
  /**
   * 그림 아래 **한 줄 설명**을 여기서 그릴까.
   *
   * 🔴 **기본은 `true` = 종전 그대로**다. 장비 관점 콘솔·태블릿·패드는 이 프롭을 안 넘긴다.
   * 🔴 **`false` 로 끄는 곳은 장비 탭 하나다**(`EquipmentSheet`). 거기는 한 화면에 그림이
   *    3~4장 서서 **같은 말이 그림마다 찍혔고**, 더 나쁘게는 그 줄의 길이가 블록 폭을 밀어
   *    스텝마다 그림이 좌우로 흔들렸다(eric 2026-09-22 — 「텍스트가 추가되고 그러면서 계속
   *    뒤틀려」). ⇒ 그쪽은 줄을 그림 «밖»에 한 번만 세운다. 글자는 같은 `activityText()` 가 만든다.
   */
  activityNote?: boolean | undefined
  /**
   * 일하는 부품의 **블록 전체 강조**(`.fig-now` 안쪽 2px 테 + 움직이는 중이면 숨쉬기 · `mark()`)를 걸까.
   *
   * 🔴 **기본은 `true` = 종전 그대로**다 — 장비 관점 앱 · 모듈 패드의 「전체」 보기는 부품 여럿 중 «어느 것이» 일하는지를
   *    이 테로 가른다. 그쪽은 이 프롭을 안 넘긴다.
   * 🔴 **`false` 로 끄는 곳은 콘솔 장비 판 하나다**(`EquipmentSheet` · eric 2026-10-11 「왜 자꾸 전체가 사각형으로 표시돼? 전체가 다
   *    깜빡거리니 이상하잖아」). 거기는 블록이 장비 하나를 통째로 그린 한 장(염색기 정면도 = 피펫 + 트레이 + 통)이라 테가
   *    칸 전체를 두르고 깜빡였다 — 「처리 중 ○○」 단계 띠와 그림 안 표지(챔버 ▶ · 피펫 내려감 · 진행 중 칸 테)가 이미 같은 말을 한다.
   * ⚠ 칸 «안»의 개별 표지는 그대로다 — 이 깃발은 블록을 감싸는 겉겹 하나만 끈다.
   */
  blockHighlight?: boolean | undefined
  /**
   * **같은 사물은 시료 탭의 그림으로 그린다**(eric 2026-09-27 「장비 쪽에 이 그림들, 시료 쪽의 그림으로
   * 대체해줘」).
   *
   * | 부품 | 끔(종전) | 켬 |
   * | --- | --- | --- |
   * | 트레이 칸의 챔버 | SVG 셸 | 굵은 먹색 테두리 사각형(`chamberOutline` — 3D 아님 · eric 2026-09-28) |
   *
   * ⚠ TC 랙(`tc`) · 피펫 · 시약(`pip`)을 데크 랙(`DeckRackFigure`)으로 바꿔 그리던 길은 걷었다(2026-10-03).
   *   켜는 곳(장비 탭)이 그 두 칸을 격자에서 빼서(`EquipmentSheet` 의 `SUPPLY_RACK_BLOCKS` · eric 2026-10-02 「소모품 그림이랑
   *   겹치잖아」) 어디서도 닿지 않았다 — 랙 그림은 소모품 판 한 곳이다. 이 깃발과 무관하게 두 칸은 종전 그림이다.
   *
   * 🔴 **기본은 꺼짐 = 종전 그대로**다. 켜는 곳은 통합 콘솔 장비 탭(`EquipmentSheet`) 하나다 —
   *    장비 관점 콘솔·태블릿·모듈 패드는 `ModuleFigureView` 길이라 이 깃발을 안 켠다.
   * 🔴 **장비 탭에는 3D 챔버가 없다**(eric 2026-09-28 「시료 부분에서만 3d 쓰고.. 장비에서는 저거 쓰지마」).
   * ⚠ 모듈 본체(원심 로터 · 어셈블러 · 셰이커)는 시료 탭에 대응 그림이 없어 **그대로**다.
   */
  realFigures?: boolean | undefined
  /**
   * 어셈블러 본체(`mod`)를 **장치 정면도 한 장(압착 TC 장착 + UV 노출)** 으로 그린다(eric 2026-10-07 「두개.. 합쳐」 · 「정면에서」).
   * 콘솔 장비 판(`EquipmentSheet`)이 넘긴다 — 지금(`phase` · TC 장착 → UV → TC 분리) · 아래 한 줄은 서버 모듈 상태에서 그쪽이 가른다. UV · 스테이지 위 칸은 여기
   * 종전 재료(서버 트레이 뷰 · 텔레메트리) 그대로다. 안 넘기면 종전 평면도(장비 관점 앱 · 패드).
   */
  station?:
    | {
        supplies?: SuppliesView | undefined
        phase: StationPhase
        statusLabel: string
        /** 3축 그리퍼 내려감 · 쥠(`asmStationModel.ts`) — 없으면 종전 규칙(`idle` 아니면 내려옴) */
        down?: boolean | undefined
        holding?: boolean | undefined
        /** 그림 안 그리퍼 — 장비 판이 겹침 그리퍼(`AsmGripper`)를 세우면 끈다 */
        gripper?: boolean | undefined
        /** 모듈 앱에서는 장치 내용에 맞춰 빈 윗공간을 덜어낸다. */
        fitDevice?: boolean | undefined
        /** TC 랙 남은 수(모듈 소모품 `tc_remaining`) — 넘기면 왼쪽에 TC 랙을 그린다(eric 2026-10-08 「염색기처럼 왼쪽에 tc rack」) */
        tcRemaining?: number | null | undefined
      }
    | undefined
  /**
   * 염색기 셰이커 칸(`shaker`)을 **위 피펫 툴 정면도(위아래로만) + 아래 종전 트레이 평면도** 한 장으로 그린다
   * (eric 2026-10-07 「염색기를 정면도로」 → 「트레이는 기존대로 평면도로」 · 「피펫 장비는 정면도인데.. 위아래로만」 · 「3축 틀 안보여줘도 돼」).
   * 콘솔 장비 판이 넘긴다 — 지금(`phase` · 서버 시퀀스 · 상태) · 액체 방향 · 아래 한 줄(동작 이름 · 스텝). 챔버 · 팁 · 트레이 · 교반 · 자리 · 후문은
   * 여기 종전 재료(`preCells` · `shakerModel.ts` · `snapshot.tip`) 그대로다. 안 넘기면 종전 셰이커 평면도(장비 관점 앱 · 패드).
   */
  front?:
    | {
        renderTray?: ((plan: ReactNode) => ReactNode) | undefined
        renderDeck?:
          ((plan: ReactNode, waste: ReactNode, now: StainDeckNow) => ReactNode) | undefined
        phase: StainFrontPhase
        statusLabel: string
        arrow?: 'up' | 'down' | undefined
        /** 폐기통 동작 — 토출 · 팁 버림(`stainFrontWaste`). 툴을 끈 판의 통 표현이 고른다 */
        waste?: StainWasteKind | undefined
        /** 그림 안 피펫 툴 — `false` 면 끈다(콘솔 장비 판 · 판 위 겹침 피펫 `StainPipette` 가 대신 · eric 2026-10-13). 기본 켬 */
        tool?: boolean | undefined
      }
    | undefined
  /**
   * 6축 칸을 **가로 라인(원심분리기 · 어셈블러 · 염색기 정거장 + 레일 위 6축)** 으로 그린다(eric 2026-10-08). 콘솔 장비 판이 넘긴다 —
   * 세 모듈 스냅샷(핸드오프 자리 · 반출/반입 상태)과 끊김 여부. 안 넘기면 종전 팔 그림 한 장
   */
  robotLine?: { modules: readonly ModuleSnapshot[]; stale: boolean } | undefined
  /**
   * 비전 실영상의 연결 상태를 바깥에 알린다 — **장비별 보기의 영상 칸 머리 줄**(`VisionBar`)이 읽는다(2026-10-01 회의).
   * 🔴 넘기면 영상 위 정보 글자(`● LIVE …`)를 끈다 — 머리 줄이 같은 사실을 말한다(상세 카드 비전 칸 `VisionPane` 과 같은 규칙).
   * 🔴 **기본은 안 넘김 = 종전 그대로**다. 장비 관점 콘솔 · 태블릿 · 모듈 패드는 안 넘긴다.
   */
  onVisionState?: ((state: VisionStreamState) => void) | undefined
}

/**
 * 「전체」 보기의 선택 조각 — **장비 관점 콘솔용 깃발**이다.
 *
 * 🔴 하나도 켜지 않은 것이 기본이고, 그때 화면은 시료 관점 콘솔·패드·태블릿과 **한 픽셀도 다르지 않다.**
 *    새 조각을 더할 때도 기본값은 반드시 `false` 다.
 */
export interface OverviewParts {
  /** 맨 윗줄에 3축 로봇 레일을 그린다 */
  gantry?: boolean | undefined
  /** 레일 «아래»에 비전 카메라 진입 아이콘을 붙인다(레일 위에 얹지 않는다 — eric 지시 2026-09-18) */
  visionIcon?: boolean | undefined
  /**
   * 트레이를 「작업 전 / 작업 후」 **두 장**으로 그린다. 두 장이 나란히 놓이므로 이름도
   * 「트레이」가 아니라 위치 이름(`TRAY_LABEL_KEY`)으로 바뀌고, 범례에 「작업 후」 줄이 붙는다.
   * ⚠ 염색기는 원래부터 뒷자리가 셰이커라 트레이가 늘지 않는다(§16-2) — 이름과 범례만 따라간다.
   */
  postTray?: boolean | undefined
  /**
   * **스텝마다 「지금 어느 구성요소가 일하는가」를 그림 위에 얹는다**(eric 지시 2026-09-21 —
   * 「시료가 로터에 가고, 완료 트레이에 가고 … 배치의 스텝별로 모니터링 그림 형태로」).
   *
   * 🔴 **그림의 생김새·배치는 한 픽셀도 바뀌지 않는다.** 얹는 것은 ⓐ 강조 테두리 ⓑ 글리프
   *    ⓒ 한 줄 설명 ⓓ 칸의 소재뿐이다. 새 색을 들이지 않는다(무채색 · 색만으로 구분하지 않는다).
   * 🔴 **끄면 종전과 바이트 단위로 같다.** 시료 관점 콘솔·패드·태블릿은 이것을 켜지 않는다 —
   *    `stepActivity.test.tsx` 가 「콘솔 경로 렌더에 강조가 0개」를 고정한다.
   * 🔴 도는 배치가 없거나 구독이 stale 이면 **저절로 아무것도 안 그린다**(`stepActivity()`).
   */
  stepActivity?: boolean | undefined
}

/**
 * 「전체」 보기의 그림 ↔ 부품 키 대응.
 * 🔴 탭 키와 **같은 값**이다. 여기서 새 이름을 만들면 탭과 그림이 갈린다.
 */
const OVERVIEW_KEYS: Record<
  Exclude<MonitoringDeviceKey, 'robot'>,
  { pre: string; body: string; post: string }
> = {
  cent: { pre: 'trays', body: 'cent', post: 'trays' },
  asm: { pre: 'trays', body: 'mod', post: 'trays' },
  // 염색기는 트레이가 한 장이고 뒷자리에 셰이커가 온다(§16-2)
  stain: { pre: 'tray', body: 'pip', post: 'shaker' },
}

/**
 * 포커스 뷰 — 고른 컴포넌트만 크게. 🔴 **그림에 제목을 붙이지 않는다**(탭이 이름 역할 · §14-3).
 * 트레이만 예외다 — 두 장이 나란히 놓여서 어느 쪽인지 말해야 한다.
 */
export function FocusFigure({
  deviceKey,
  component,
  snapshot,
  chambers,
  tray,
  running,
  canViewPii,
  visionFeed,
  onSelectComponent,
  selectableComponents = [],
  overview = {},
  trayStage = false,
  activityNote = true,
  blockHighlight = true,
  realFigures = false,
  onVisionState,
  station,
  front,
  robotLine,
}: FocusFigureProps): ReactNode {
  // 파지 상태는 계약 필드다(`ModuleSnapshot.grips`). ⚠ 빈 배열(그리퍼 없음)과 미발행(판정 불가)은 다르다
  const holding = (snapshot?.grips ?? []).some((grip) => grip.state === 'holding')

  /*
   * 🔴 **작업 줄 3행과 «같은 보관소»를 읽는다**(`useModuleProgress` ↔ `useBatchProgress`).
   *    두 곳이 다른 말을 할 수 없게 하는 것이 규약이라 여기서 따로 조회하지 않는다.
   * 🔴 **훅은 조건 없이 부른다**(훅 규칙). 끄고 켜는 것은 «결과»이지 호출이 아니다 —
   *    `overview.stepActivity` 가 꺼져 있으면 아래에서 `NO_ACTIVITY` 로 접는다.
   * ⚠ 쿼리 제공자가 필요 없다 — 구독 보관소를 `useSyncExternalStore` 로 읽을 뿐이다.
   */
  const progress = useModuleProgress(snapshot?.module_id)
  /* 서버 트레이 뷰(모니터링 컨테이너가 내린다 · 없으면 `undefined`) — 훅이라 조기 반환보다 앞에서 읽는다 */
  const trayView = useContext(TrayViewContext)
  /* 염색기 «직전 분주 칸» — 컨테이너가 분주 기록으로 내린다(`StainLatestWellContext`). 정면도(`front`)만 쓴다 */
  const latestDispensedWell = useContext(StainLatestWellContext)
  const activity: StepActivity =
    overview.stepActivity === true ? stepActivity(deviceKey, snapshot, progress) : NO_ACTIVITY
  const live = activity.components.length > 0

  if (deviceKey === 'robot') {
    /*
     * 콘솔 장비 판(`robotLine`) — 가로 라인(정거장 셋 + 레일 위 6축 · eric 2026-10-08). 안 넘기면 팔 그림 한 장(장비 관점 앱 · 패드).
     * 🔴 관절값이 없으면 기준 자세 점선 + 「자세 미발행」 — 움직이는 척하지 않는다(`RobotArm` · 2026-10-06)
     */
    if (robotLine !== undefined)
      return <RobotLine modules={robotLine.modules} snapshot={snapshot} stale={robotLine.stale} />
    return <RobotArm snapshot={snapshot} />
  }

  const labels = TRAY_LABEL_KEY[deviceKey]
  /**
   * 🔴 라벨을 받아서 그린다. **「전체」는 「트레이」**(한 장뿐이라 앞 글자로 구분할 이유가 없다 —
   *    eric 지시 2026-09-18), **「트레이」 탭은 위치 이름**(두 장을 견주므로 구분이 필요하다).
   * ⚠ `TRAY_LABEL_KEY` 값은 표시용이 아니라 **서버 `position` 문자열과 대조하는 프로토콜 값**이다.
   *   화면에 쓰는 이름과 갈라 둔 이유가 그것이다(`ko.ts` 주석 참조).
   */
  /*
   * 🔴 **「모른다」를 말하는 한 줄.** 칸 배정이 없을 때 반드시 붙는다 — 이것이 A 안의 전부다.
   *    ⚠ 「비어 있다」와 「모른다」는 다르다. 트레이에 시료가 **들어 있는데** 우리가 어느 칸인지
   *      모르는 것이고, 문구 없이 빈 격자만 그리면 화면이 「비었다」고 단정하게 된다.
   *      이 코드베이스가 `inconclusive` 를 제3값으로 두는 것과 같은 축이다.
   */
  const unknownAssignment = tray === undefined
  const assignmentNote = unknownAssignment ? (
    <p className="fignote">{t('monitoring.noSlotAssignment')}</p>
  ) : null

  /*
   * 🔴 **칸이 지금 어디 있는가.** 도는 배치가 있을 때만 값이 찬다 — 없으면 종전대로
   *    전부 빈 격자이고 「칸 배정 정보 없음」이 붙는다.
   * ⚠ 이 값은 **임시 목**이다(`temporaryStepMock.ts`). 서버가 칸의 소재를 주기 시작하면
   *   그 파일만 걷는다 — 아래 그림 코드는 「칸 이름이 여기 있다」만 알 뿐 임시인 줄 모른다.
   */
  const flow = temporaryChamberFlow(deviceKey, activity.step, activity.state, live)
  /*
   * 🔴 **서버 트레이 뷰가 있으면 그것이 이긴다**(`GET /monitoring/trays` · eric 2026-10-07). 위 임시 흐름은 스텝을 칸 수로 나눈
   *    나머지로 자리를 지어 내 결과 트레이 ↔ 시작 트레이를 왔다 갔다 했다 — 서버 뷰는 쌓이는 실행 기록으로 세어 앞으로만 간다.
   *    모니터링 컨테이너(live)만 이 값을 내린다. 작업이 시작 전이면 종전 그림 그대로다.
   */
  const serverTrays =
    trayView !== undefined && trayView.batch?.started === true ? trayView : undefined
  /** 이름표 옆 「3/9」 — 서버 뷰가 있을 때만(지어내지 않는다) */
  const countedBase = (label: string, side: 'pre' | 'post'): string => {
    const count =
      serverTrays === undefined ? undefined : trayViewCount(serverTrays, deviceKey, side)
    return count === undefined ? label : `${label} ${String(count.count)}/${String(count.capacity)}`
  }
  /* 비전 검사 꼬리 — 서버가 그 트레이의 검사 기록을 줬을 때만(eric 2026-10-07 「출발지 챔버 체크하고 종료된 챔버도 비전으로 체크하지?」) */
  const visionOf = (side: 'pre' | 'post'): { status: string; text: string } | undefined => {
    const inspected = serverTrays?.trays.find(
      (item) => item.tray === trayNameFor(deviceKey, side),
    )?.inspected
    if (inspected == null) return undefined
    return {
      status: inspected.status,
      text: t(
        inspected.status === 'passed'
          ? 'monitoring.tray.visionPassed'
          : inspected.status === 'failed'
            ? 'monitoring.tray.visionFailed'
            : 'monitoring.tray.visionRunning',
      ),
    }
  }
  /** 이름표 전문(보조기술용 `label`) — 수 + 비전 꼬리 */
  const counted = (label: string, side: 'pre' | 'post'): string => {
    const vision = visionOf(side)
    return vision === undefined
      ? countedBase(label, side)
      : `${countedBase(label, side)} · ${vision.text}`
  }
  /* 보이는 이름표 — 비전 꼬리는 색(성공 초록 · 위험색)을 입은 조각(eric 2026-10-07 「디자인팀이 제공한 초록색 계통으로」) */
  const figLabel = (label: string, side: 'pre' | 'post') => {
    const vision = visionOf(side)
    return (
      <div className="figlbl">
        {countedBase(label, side)}
        {vision === undefined ? null : (
          <span className={`figlbl__vision figlbl__vision--${vision.status}`}>
            {' '}
            · {vision.text}
          </span>
        )}
      </div>
    )
  }
  /**
   * 🔴 **칸 이름만 옮긴다 — 시료 ID 를 적지 않는다.** 어느 시료가 어느 칸인지는 서버에 없고
   *    (BR-31) eric 이 트레이 단위로 움직인다고 정했다. 걷어낸 가짜를 되살리지 마라.
   */
  const flowCells = (here: readonly string[], moved: readonly string[]): TrayCellView[] =>
    TRAY_SLOTS.map((slot) => {
      if (here.includes(slot)) return { slot, present: true }
      if (moved.includes(slot)) return { slot, present: false, verdict: 'moved' as const }
      return { slot, present: false }
    })

  /**
   * @param labelled 그림 «안»에 이름표를 그릴까. 🔴 **기본 `true` = 종전 그대로**다.
   *   끄는 곳은 콘솔 장비 탭의 갈린 트레이 블록 하나뿐이고(`trayPre`/`trayPost`), 거기서는
   *   블록 머리(`h3.figlbl`)가 이름을 말해 두 겹이 된다.
   */

  /* 작업 «전» 트레이의 아홉 칸 — 트레이 그림과 염색기 정면도(`front`)가 같은 재료를 읽는다 */
  const preCells: readonly TrayCellView[] =
    serverTrays !== undefined
      ? trayViewCells(serverTrays, deviceKey, 'pre')
      : live
        ? flowCells(flow.inlet, [...flow.inModule, ...flow.outlet])
        : trayCells(tray, chambers, deviceKey, 'pre', canViewPii)
  const preTray = (name: string, labelled = true) => {
    const label = counted(name, 'pre')
    return (
      <div className="figbox">
        {labelled ? figLabel(name, 'pre') : null}
        <TrayFigure
          cells={preCells}
          chamberOutline={realFigures}
          counterweightLoaded={
            deviceKey === 'cent' &&
            (serverTrays !== undefined
              ? serverTrays.trays.find((item) => item.tray === 'C')?.counterweight?.present === true
              : live || (!unknownAssignment && running))
          }
          label={label}
        />
        {live || serverTrays !== undefined ? null : assignmentNote}
      </div>
    )
  }
  const postTray = (name: string, labelled = true) => {
    const label = counted(name, 'post')
    return (
      <div className="figbox">
        {labelled ? figLabel(name, 'post') : null}
        <TrayFigure
          cells={
            serverTrays !== undefined
              ? trayViewCells(serverTrays, deviceKey, 'post')
              : live
                ? flowCells(flow.outlet, [])
                : trayCells(tray, chambers, deviceKey, 'post', canViewPii)
          }
          chamberOutline={realFigures}
          label={label}
        />
        {live || serverTrays !== undefined ? null : assignmentNote}
      </div>
    )
  }
  const trayOnly = t('monitoring.tray.one')

  // 레일 0~600 mm 를 0~1 로. 값이 없으면 홈(0)에 둔다 — 임의 위치를 그리지 않는다
  const axisRatio = (telemetryNumber(snapshot, 'gantry_x_mm') ?? 0) / 600
  /**
   * 레일의 이름표. 🔴 **염색기는 「피펫 3축」이다**(§16-1 — 3축이 곧 피펫 캐리어다).
   * 그림 · 접근성 이름 · 강조 설명이 **한 글자에서 나와야** 한글 표기가 갈리지 않는다.
   */
  const railName = t(deviceKey === 'stain' ? 'figTab.pip3' : 'figTab.gantry')
  const gantry = (
    <GantryFigure
      holding={holding}
      kind={deviceKey === 'stain' ? 'pipette' : 'grip'}
      label={railName}
      name={railName}
      position={axisRatio}
    />
  )

  /*
   * 🔴 **「비전」 탭은 «언제나» 있다 — eric 지시 2026-09-21 「비전 탭은 항상 있어야 해」.**
   *    ⇒ 비는 것은 여기, **탭 안의 내용**이다. 영상 자료가 없으면 오버레이만 남고 판정 박스는
   *    0개이므로, 트레이의 「칸 배정 정보 없음」과 같은 결로 **왜 비었는지**를 한 줄 적는다.
   * 🔴 **가짜 판정 박스를 되살리지 마라.** 탭이 돌아온 것이지 fixture 판정이 돌아온 것이 아니다.
   *    `verdict: null` 을 `'pass'` 로 접던 사고(아래 `visionCells` 머리말)를 다시 열지 마라 —
   *    live 에서 이 탭 안은 **비어 있고 사유만 있다.**
   */
  /*
   * 🔴 **실영상은 「빈 자리」에만 선다.** 순서가 규약이다:
   *    ⓐ `visionFeed` 가 있다(= demo) → **종전 그대로.** fixture 영상·판정이 그대로 돈다
   *    ⓑ 없고 스트림 이름이 설정돼 있다 → 그 자리에 **플레이어**가 선다
   *    ⓒ 없고 설정도 없다 → 종전대로 「카메라 영상 연결 없음」
   *    demo 를 플레이어로 갈아치우지 마라 — 「일단 붙인다」(eric 2026-09-21)는 ⓑ 한 칸이다.
   * ⚠ 어느 길이든 **판정 박스는 늘어나지 않는다.** 플레이어는 박스를 아예 그리지 않는다.
   */
  const liveStream = visionFeed === undefined ? visionStreamFor(deviceKey) : undefined

  /**
   * 🔴 **비전만 강조를 «겉겹»이 아니라 «제 상자»에 건다** — eric 2026-09-22
   *    「영상이 그림 바뀌면서 깜빡거리는데 수정해줘」.
   *
   * ## 왜 깜빡였나 — `<video>` 가 스텝마다 «다시 붙고» 있었다
   *
   * 아래 `detail()`/`mark()` 는 그 부품이 일할 때«만» 그림을 `div.fig-now` 로 감싼다.
   * 감싸기 전(`node`)과 후(`<>{div}{p}</>`)는 **같은 자리에 선 다른 모양의 트리**라 React 가
   * 아래를 통째로 버리고 새로 만든다. 비전은 진행 스텝의 `inspect` 마다 켜졌다 꺼지므로
   * (`stepActivityModel` — `inspect: 'vision'`) 배치가 도는 내내 **언마운트 → 마운트**가
   * 되풀이됐고, 그때마다 go2rtc 연결이 끊겼다 다시 붙어 검은 화면이 한 번씩 지나갔다.
   *
   * ⚠ 트레이·로터는 SVG 라 다시 그려도 눈에 안 띈다. **비용이 보이는 것은 영상뿐**이라
   *   여기만 고친다(`detail()`/`mark()` 는 다른 부품에서 종전 그대로다).
   *
   * ## 고친 방식 — 상자는 «늘» 있고 **클래스만** 오간다
   *
   * 🔴 원소를 더하거나 빼지 않는다. `figbox` 한 장이 늘 서고 강조는 그 위의 클래스다 —
   *    React 가 버릴 것이 없으니 `<video>` 가 살아 있다.
   * 🔴 **강조를 버려서 고친 것이 아니다.** 켜질 때 `.fig-now` 와 글리프가 종전대로 붙는다
   *    (글리프는 조건부지만 그림 «앞»의 고정 자리라 그림의 색인이 안 밀린다).
   * ⚠ 강조가 `.figbox` 자신에 걸린다 — 종전 겉겹은 `.figbox` 를 꼭 감싸 크기가 같았으므로
   *   보이는 테두리 자리는 그대로다.
   */
  const visionActive = live && isActive(activity, 'vision')
  const vision = (
    <div
      className={cx(
        'figbox',
        visionActive && 'fig-now',
        visionActive && activity.moving && 'fig-now--moving',
      )}
      {...(visionActive ? { 'aria-label': t('activity.aria', { name: t('figTab.vision') }) } : {})}
    >
      {visionActive ? (
        <span aria-hidden="true" className="fig-now__glyph">
          {activity.moving ? '◐' : '❙❙'}
        </span>
      ) : null}
      {liveStream !== undefined ? (
        <VisionStream
          label={t('figTab.vision')}
          moduleKey={deviceKey}
          stream={liveStream}
          {...(onVisionState === undefined ? {} : { onState: onVisionState, meta: false })}
        />
      ) : (
        <>
          <VisionFrame
            camera={visionFeed?.camera_id ?? t('mask.none')}
            cells={visionCells(deviceKey, chambers)}
            label={t('figTab.vision')}
            live={visionFeed?.live === true && running}
            liveLabel={t('monitoring.live')}
            overlay={deviceKey}
            resolution={
              visionFeed === undefined
                ? t('mask.none')
                : `${String(visionFeed.width)}×${String(visionFeed.height)}`
            }
          />
          {visionFeed === undefined ? (
            <p className="fignote">{t('monitoring.noVisionFeed')}</p>
          ) : null}
        </>
      )}
    </div>
  )

  /*
   * 🔴 로터 네 자리는 **시료↔칸 대응 위에 선다.** 그 대응이 없으면 한 자리도 못 채우므로
   *    전부 비우고 「적재 정보 없음」을 적는다 — 빈 네 자리를 「로터가 비었다」로 읽으면 거짓이다.
   */
  /*
   * 🔴 **도는 배치가 있으면 로터 네 자리를 「지금 안에 있는 칸」으로 채운다.**
   *    없으면 종전대로 전부 비고 「적재 정보 없음」이 붙는다.
   * 🔴 **칸 이름만 적는다**(`A1` · `A2` …). `sublabel`(시료 ID) 자리는 **비워 둔다** — BR-31.
   */
  const liveRotor = (live || serverTrays !== undefined) && deviceKey === 'cent'
  const rotorSlotViews =
    serverTrays !== undefined && deviceKey === 'cent'
      ? trayViewRotor(serverTrays)
      : liveRotor
        ? ([1, 2, 3, 4] as const).map((index) => {
            const slot = flow.inModule[index - 1]
            return slot === undefined
              ? { index, loaded: false }
              : { index, slot, loaded: true, active: activity.moving && running }
          })
        : rotorSlots(tray, chambers, canViewPii, running)
  const rotor = (size: 'md' | 'lg') => (
    <>
      <RotorFigure
        label={moduleLabel('cent')}
        size={size}
        slots={rotorSlotViews}
        /* 🔴 서버 트레이 뷰가 있으면 그 「회전 중」(`c_r{k}_spin` 진행 중)만 돈다 — 싣고 꺼내는 동안은 멈춘 그림. 없으면 종전대로 실행 중 */
        spinning={serverTrays?.centrifuge?.spinning ?? running}
      />
      {unknownAssignment && !liveRotor ? (
        <p className="fignote">{t('monitoring.noRotorLoad')}</p>
      ) : null}
    </>
  )

  /* 🔴 서버 트레이 뷰가 있으면 그 「UV 노출 중」(`expose_uv` 스텝 진행 중)이 먼저 — 장비가 UV 세기를 안 보내 종전엔 늘 꺼짐이었다(eric 2026-10-07) */
  const uvOn =
    serverTrays?.assembler?.uv ?? (telemetryNumber(snapshot, 'uv_intensity_mw_cm2') ?? 0) > 0
  /*
   * 🔴 「지금 어셈블러 안에 있는 칸」도 칸 축 위에 선다 — 없으면 **표시하지 않는다.**
   *    `slot` 이 없는 행(= live)은 여기 걸리지 않는다(`whereOf` 가 `undefined` 를 낸다).
   */
  /*
   * live 에서는 칸 축(`slot`)이 없어 위 줄이 늘 비었다 — 배치가 어셈블러에서 돌아도 스테이지가 빈 채였다
   * (eric 2026-09-22 「진행 중인 챔버도 어셈블러에 보이게 해줘, 지금 아무것도 안 보여」).
   * ⇒ 트레이 두 장과 **같은 임시 소재(`flow`)** 에서 「지금 장비 안」 칸을 읽는다. 어셈블러는 한 칸씩
   *    지나므로 `inModule` 은 0~1개다. 목을 걷을 때 트레이와 같이 걷힌다(`temporaryStepMock.ts`).
   */
  const inAssemblerSlot =
    serverTrays !== undefined
      ? trayViewAssemblerSlot(serverTrays)
      : live
        ? flow.inModule[0]
        : chambers.find((row) => whereOf(row, 'asm') === 'inModule')?.slot
  const stationFigure = (tcControl?: { label: string; onClick: () => void }) =>
    station === undefined ? null : (
      <AssemblerStationLine
        tcControl={tcControl}
        down={station.down}
        gripper={station.gripper ?? true}
        fitDevice={station.fitDevice}
        holding={station.holding}
        label={moduleLabel('asm')}
        phase={station.phase}
        statusLabel={station.statusLabel}
        tcLabel={'tcRemaining' in station ? t('monitoring.eqRack.tc') : undefined}
        wasteLabel={'tcRemaining' in station ? t('monitoring.waste') : undefined}
        tcRemaining={station.tcRemaining}
        uvOn={uvOn}
        {...(inAssemblerSlot === undefined ? {} : { chamberSlot: inAssemblerSlot })}
        /*
          챔버 이름은 늘 선다 — 파지 걸음에도 지우지 않는다. 종전에는 겹침 그리퍼(`AsmGripper`)가 쥔 챔버면 이름을 그 칩에 맡기고
          여기서 껐는데, 겹침 그리퍼는 2026-10-14 에 걷혀(`EquipmentSheet`) 이름 말할 곳이 없어졌다(2026-10-26 히나 검토)
        */
      />
    )
  const assembler =
    station === undefined ? (
      <AssemblerFigure
        label={moduleLabel('asm')}
        uvLabel={t(uvOn ? 'monitoring.uvOn' : 'monitoring.uvOff')}
        uvOn={uvOn}
        {...(inAssemblerSlot === undefined ? {} : { chamberSlot: inAssemblerSlot })}
      />
    ) : /* 콘솔 장비 판 — 압착 + UV 정면도 한 장. UV · 스테이지 위 칸은 위와 같은 재료다 */
    station.supplies === undefined ? (
      stationFigure()
    ) : (
      <ModuleSupplyStrip
        moduleKey="asm"
        supplies={station.supplies}
        renderContent={stationFigure}
      />
    )

  /* 셰이커 — 🔴 응답 값만 읽는다(`shakerModel.ts`). 종전에는 `running` 으로 자리·래치·헤드를 «지어» 그렸다 */
  const shakerState = shakerView(snapshot)
  const stopLabels = {
    rear: t('monitoring.stop.rear'),
    work: t('monitoring.stop.pipetting'),
    front: t('monitoring.stop.front'),
  } as const
  const shaker =
    front === undefined ? (
      <ShakerFigure
        door={shakerState.door}
        doorLabel={shakerState.doorLabel}
        label={t('figTab.shaker')}
        latch={shakerState.latch}
        shaking={shakerState.shaking}
        statusLabel={shakerState.statusLabel}
        stop={shakerState.stop}
        stopLabels={stopLabels}
        tray={shakerState.tray}
      />
    ) : (
      /*
       * 콘솔 장비 판 — 위에 피펫 툴 정면도(위아래로만), 아래에 종전 트레이 평면도(eric 2026-10-07). 평면도 노드는 왼쪽 트레이 칸이 쓰던
       * `preTray` 그대로(이름표 없이 · 「칸 배정 정보 없음」 한 줄도 그 안에 있다) · 지금 다루는 칸은 서버 트레이 뷰의 ▶(`active`) ·
       * 팁 장착은 서버 `tip.mounted`(칸 부재 = 모름)
       */
      <StainingFrontLine
        arrow={front.arrow}
        binLabels={{
          /* 팁 랙 두 장(위 1 · 아래 2 · eric 2026-10-12 「팁 랙 두 개로 상하로 나눠서」) */
          tips: t('monitoring.eqRack.tip1'),
          tips2: t('monitoring.eqRack.tip2'),
          reagent: t('monitoring.eqRack.tube'),
          waste: t('monitoring.stainFront.bin.waste'),
        }}
        /* 「후문 닫힘」 글자는 걷었다(eric 2026-10-08 「후문 닫힘 텍스트 빼줘」) — 후문 상태는 6축 라인의 뒷문 모양이 말한다 */
        label={t('monitoring.eqsheet.stainFront')}
        phase={front.phase}
        plan={
          front.renderTray === undefined
            ? preTray(t('figTab.tray'), false)
            : front.renderTray(preTray(t('figTab.tray'), false))
        }
        renderDeck={front.renderDeck}
        shaking={shakerState.shaking}
        statusLabel={front.statusLabel}
        tipMounted={snapshot?.tip === undefined ? null : snapshot.tip.mounted}
        tool={front.tool ?? true}
        waste={front.waste}
        well={stainFrontWell(preCells, latestDispensedWell)}
        {...(shakerState.stop === null
          ? {}
          : { stopLabel: t('monitoring.stainFront.stop', { stop: stopLabels[shakerState.stop] }) })}
      />
    )

  /*
   * 팁 랙 그림은 「쓴 칸」을 비운다. 남은 수와 총량이 다 있어야 계산된다 — 없으면 0(안 쓴 것).
   *
   * ⚠ **칸 이름은 `tips_remaining`(복수)** 이다 — 서버가 그 이름으로만 낸다(2026-09-21 실측 ·
   *   module-core `_CONSUMABLE_FIELD`). 종전 `tip_remaining` 은 어느 응답에도 없는 이름이었다.
   * 🔴 **`tip_capacity`(총량)는 «어느 경로에도 없다».** 계약에도 실물에도 없어 이 그림은
   *    지금 언제나 「안 쓴 것」으로 선다. 🔴 **그 자리를 추정으로 메우지 마라** — 랙 크기를
   *    상수로 박으면 화면이 서버가 말하지 않은 것을 단언한다. 총량 칸은 백엔드 요청 건이다
   *    (`docs/backend-requests.csv` 에 아직 행이 없다). 잔량 «수치»는 사이드 패널 「팁」 줄이 그대로 보인다.
   */
  const tipCapacity = consumableNumber(snapshot, 'tip_capacity')
  const tipRemaining = consumableNumber(snapshot, 'tips_remaining')
  const tipsUsed =
    tipCapacity === undefined || tipRemaining === undefined
      ? 0
      : Math.max(0, tipCapacity - tipRemaining)
  const reagentBay = (
    <ReagentBayFigure
      ambientCaption={t('monitoring.bay.ambient')}
      chilledCaption={t('monitoring.bay.chilled')}
      label={t('figTab.pip')}
      tipCaptions={[t('monitoring.tipRack1'), t('monitoring.tipRack2')]}
      tipsUsed={tipsUsed}
      wasteLabel={t('monitoring.waste')}
    />
  )

  const tcRack = (
    <TcRackFigure
      label={t('figTab.tc')}
      remaining={consumableNumber(snapshot, 'tc_remaining') ?? 0}
    />
  )

  /*
   * 🔴 **강조는 그림을 «감쌀» 뿐 그림을 고치지 않는다.** 테두리·글리프·글자만 더한다 —
   *    새 색을 들이지 않고(무채색) 도형도 바꾸지 않는다(§1-2). 꺼져 있으면 `node` 가 그대로 나간다.
   * 🔴 글리프는 `StatusBadge` 와 **같은 어휘**다 — `◐`(작업 중) · `❙❙`(멈춤).
   */
  const mark = (key: string, name: string, node: ReactNode): ReactNode => {
    /* 블록 전체 강조를 끈 판(콘솔 장비 판)은 감싸지 않는다 — `blockHighlight` 머리말 */
    if (!blockHighlight || !live || !isActive(activity, key)) return node
    return (
      <div
        aria-label={t('activity.aria', { name })}
        className={cx('fig-now', activity.moving && 'fig-now--moving')}
      >
        <span aria-hidden="true" className="fig-now__glyph">
          {activity.moving ? '◐' : '❙❙'}
        </span>
        {node}
      </div>
    )
  }

  /**
   * 그림 옆 한 줄 — 「모듈 내 스텝 85/359 · move_to 시작 · 입구 트레이 3번 → 로터 3번」.
   *
   * 🔴 **앞 두 토막은 작업 줄 3행과 «글자까지 같다»** — 같은 `stripStepText()` 를 부른다.
   *    두 곳이 다른 말을 하면 안 된다는 규약을 함수 하나로 지킨다.
   * ⚠ 마지막 토막(자리)만 이 화면에 더 있고, 그 재료는 지금 임시 목이다.
   */
  const activityLine =
    live && activityNote ? (
      <p className="fignote fignote--now">{activityText(activity, progress)}</p>
    ) : null

  /**
   * 트레이 이름표 — 켜져 있으면 「작업 전 / 작업 후」를 덧붙인다.
   * 🔴 **위치 이름(`labels.pre`)을 고치지 않는다.** 그 값은 서버 `position` 과 대조하는
   *    프로토콜 값이라(`ko.ts` 머리말) 표시할 때만 합친다.
   */
  const stageLabel = (
    name: string,
    stage: 'monitoring.legend.before' | 'monitoring.legend.after',
  ) => (trayStage ? t('monitoring.tray.stage', { tray: name, stage: t(stage) }) : name)

  /*
   * 🔴 **「트레이」 탭은 두 장을 그대로 보인다.** 「전체」에서 한 장만 남긴 것(2026-09-18 eric 지시)은
   *    오버뷰 이야기이고, **상세 보기는 남는다** — 작업 전후를 견줘 보는 자리가 여기다.
   */
  /**
   * 부품 탭 한 장. 🔴 **그 부품이 지금 일할 때만** 강조와 한 줄이 붙는다 —
   *    로터를 보고 있는데 「피펫이 일한다」를 적으면 화면이 엉뚱한 말을 한다.
   */
  const detail = (key: string, name: string, node: ReactNode): ReactNode =>
    !live || !isActive(activity, key) ? (
      node
    ) : (
      <>
        {mark(key, name, node)}
        {activityLine}
      </>
    )

  if (component === 'trays')
    return detail(
      'trays',
      t(labels.pre),
      <div className="figpair">
        {preTray(stageLabel(t(labels.pre), 'monitoring.legend.before'))}
        {postTray(stageLabel(t(labels.post), 'monitoring.legend.after'))}
      </div>,
    )
  /*
   * 🔴 **트레이 «한 장»짜리 블록** — 콘솔 장비 탭이 쌍(`trays`)을 갈라 「작업 전 | 장비 | 작업 후」로
   *    놓기 위한 자리다(eric 지시 2026-09-22 · 순서는 `equipmentBlocks.ts` 가 정한다).
   * 🔴 **그리는 노드는 위와 «같은» `preTray`/`postTray` 다.** 그림을 새로 짓지 않았다 —
   *    갈린 것은 두 장을 담던 `figpair` 한 겹뿐이고, 이름표는 블록 머리가 말하므로 끈다.
   * 🔴 **강조 키는 `trays` 그대로**다. 여기서 새 키를 만들면 `stepActivity` 대응표와 갈린다.
   * ⚠ 이 두 키를 내는 곳은 `equipmentBlocks()` 하나다. 다른 화면은 `trays`·`tray` 그대로라
   *   장비 관점 콘솔·태블릿·패드가 한 픽셀도 안 바뀐다.
   */
  if (component === 'trayPre')
    return detail(
      'trays',
      t(labels.pre),
      preTray(stageLabel(t(labels.pre), 'monitoring.legend.before'), false),
    )
  if (component === 'trayPost')
    return detail(
      'trays',
      t(labels.post),
      postTray(stageLabel(t(labels.post), 'monitoring.legend.after'), false),
    )
  if (component === 'tray') return detail('tray', t(labels.pre), preTray(t(labels.pre)))
  /*
   * 🔴 **비전은 `detail()` 을 «거치지 않는다»** — 그 함수가 강조 여부에 따라 트리 모양을 바꿔
   *    `<video>` 를 다시 붙였다(위 `vision` 머리말). 강조는 이미 상자가 쥐고 있고, 여기서는
   *    **언제나 같은 모양**(상자 + 한 줄 자리)을 낸다. 한 줄은 종전과 같은 조건에서만 글자가 선다.
   */
  if (component === 'vision')
    return (
      <>
        {vision}
        {visionActive ? activityLine : null}
      </>
    )
  if (component === 'cent') return detail('cent', moduleLabel('cent'), rotor('lg'))
  if (component === 'mod') return detail('mod', moduleLabel('asm'), assembler)
  if (component === 'tc') return detail('tc', t('figTab.tc'), tcRack)
  if (component === 'shaker') return detail('shaker', t('figTab.shaker'), shaker)
  if (component === 'pip') return detail('pip', t('figTab.pip'), reagentBay)
  /*
   * 3축 로봇 포커스 뷰 — **레일을 켠 화면에서만 갈 수 있는 자리**다. 탭(`figureTabs(키,{gantry:true})`)과
   * 이 깃발이 같이 켜져야 도달한다. 꺼져 있으면 탭 자체가 없으므로 여기로 떨어지지 않는다.
   */
  if (component === RAIL_COMPONENT && overview.gantry === true)
    return (
      <GantryFocusFigure
        drop={(telemetryNumber(snapshot, 'gantry_z_mm') ?? 0) / 120}
        holding={holding}
        label={railName}
        position={axisRatio}
      />
    )

  // 「전체」 — 그림만 + 오른쪽 아래 범례. 그림을 누르면 그 부품으로 넘어간다(§14-3)
  const keys = OVERVIEW_KEYS[deviceKey]

  /**
   * 그림 한 덩이를 누를 수 있게 감싼다.
   * 🔴 `div` 에 `onClick` 을 붙이지 않는다 — `button` 이라 키보드·스크린리더로 닿는다.
   * 탭이 없는 부품이거나 호출부가 핸들러를 안 주면 **그냥 그림으로 남는다.**
   */
  const pick = (key: string, name: string, node: ReactNode, markKey = key): ReactNode => {
    // 🔴 강조를 «안쪽»에 감싼다 — 버튼이 바깥이라 누르는 자리가 안 바뀐다
    const body = mark(markKey, name, node)
    if (onSelectComponent === undefined || !selectableComponents.includes(key)) return body
    return (
      <button
        aria-label={t('figure.pick', { name })}
        aria-pressed={component === key}
        className="fig-pick"
        onClick={() => onSelectComponent(key)}
        type="button"
      >
        {body}
      </button>
    )
  }

  /*
   * 🔴 **기본 「전체」 보기에 있는 것은 트레이 한 장과 모듈 본체뿐이다**(시료 관점 · 2026-09-18).
   *    레일 · 비전 아이콘 · 「작업 후」 트레이는 **장비 관점이 `overview` 로 켤 때만** 나온다.
   *    옵션을 안 넘기면 이 함수는 예전과 똑같이 돈다 — 기본값을 바꾸지 마라.
   *    🔴 **「비전」 탭은 «언제나» 있다 — eric 지시 2026-09-21 「비전 탭은 항상 있어야 해」.**
   *       ⇒ `selectableComponents` 에 `vision` 이 늘 들어가고 **아래 카메라 아이콘도 늘 그려진다.**
   *       `overview.visionIcon === true` 하나가 그리느냐 마느냐를 정한다(= 장비 관점이냐).
   *       ⚠ 하루 전(09-20)에는 반대였다 — `MonitoringScreen` 이 `vision: visionFeeds !== undefined`
   *         로 탭을 걸러 **live 화면에서 탭도 아이콘도 사라졌고, eric 이 그것을 뒤집었다.**
   *         「자료도 없는데 왜 탭이 있나」로 그 가드를 되살리지 마라. 자료가 없을 때 비는 것은
   *         **탭 안**이고 사유는 위 `vision` 노드가 적는다.
   *       ⚠ 아래 `includes('vision')` 는 남겨 둔다 — 6축(`robot`)처럼 탭이 아예 없는 화면에서
   *         갈 곳 없는 아이콘을 막는 안전장치다.
   *    ⚠ 6축 로봇(`robot`)과 헷갈리지 마라. 저쪽은 이 함수 맨 위에서 따로 그린다.
   */
  const rail = overview.gantry === true || overview.visionIcon === true
  // 두 장을 나란히 두면 「트레이」로는 어느 쪽인지 말할 수 없다 — 위치 이름으로 바꾼다
  const preName = overview.postTray === true ? t(labels.pre) : trayOnly

  return (
    <div className={cx('overview', rail && 'overview--rail')}>
      {rail ? (
        <div className="overview__rail">
          {/*
            🔴 **레일 키는 `RAIL_COMPONENT` 하나에서 온다 — 여기에 글자를 다시 적지 마라.**
               갈라졌다가 사고가 났다: 레일이 `'gantry'` 인데 염색기 대응표는 `'pip'` 만 내서
               **피펫팅 중에 피펫이 한 번도 안 켜졌다**(2026-09-21 실물 359스텝).
            🔴 **강조를 걸 수 있는 것은 `monitoring.css` 의 `.overview__rail > .fig-now` 덕이다.**
               그 규칙이 없으면 `mark()` 가 씌우는 한 겹이 `align-items:flex-end` 아래에서
               내용 폭으로 줄어들어 **레일 선이 사라지고 피펫이 오른쪽으로 튄다**(실물 확인).
               규칙을 지우려거든 이 강조부터 걷어라.
          */}
          {overview.gantry === true ? pick(RAIL_COMPONENT, railName, gantry) : null}
          {/* 비전 카메라는 레일 오른쪽 «아래»에 붙는다 — `railCam()` 1668행 + eric 지시(y 축) */}
          {overview.visionIcon === true &&
          selectableComponents.includes('vision') &&
          onSelectComponent !== undefined ? (
            <VisionCameraButton
              corner
              onClick={() => {
                onSelectComponent('vision')
              }}
              selected={component === 'vision'}
            />
          ) : null}
        </div>
      ) : null}
      {pick(keys.pre, preName, preTray(preName))}
      {pick(
        keys.body,
        moduleLabel(deviceKey),
        <div className="figbox">
          <div className="figlbl">{moduleLabel(deviceKey)}</div>
          {deviceKey === 'cent' ? rotor('md') : deviceKey === 'asm' ? assembler : reagentBay}
        </div>,
      )}
      {/*
        🔴 **기본은 트레이 한 장이다**(시료 관점 · 2026-09-18). 남기는 쪽은 **작업 «전»(입구)** 이다 —
           그 모듈이 지금 무엇을 처리하고 있는지가 모니터링이 보려는 것이라서다.
           장비 관점은 `overview.postTray` 로 「작업 후」 한 장을 되살린다.
        ⚠ 염색기는 옵션과 무관하게 뒷자리가 셰이커다(§16-2 — 같은 트레이가 슬라이더로 자리만 옮긴다).
      */}
      {deviceKey === 'stain'
        ? pick(
            keys.post,
            t('figTab.shaker'),
            <div className="figbox">
              <div className="figlbl">{t('figTab.shaker')}</div>
              {shaker}
            </div>,
          )
        : overview.postTray === true
          ? pick(keys.post, t(labels.post), postTray(t(labels.post)))
          : null}
      {/*
        🔴 **「전체」의 한 줄은 여기 하나다.** 부품마다 붙이면 같은 말이 세 번 나온다 —
           강조는 그림이 하고, 무슨 일인지는 이 줄이 말한다.
      */}
      {activityLine}
      <div className="overview__legend">
        {/*
          범례는 «화면에 있는 것»만 설명한다. 없는 것을 설명하면 거짓말이므로
          「작업 후」 줄은 두 장 구성일 때만 붙인다.

          🔴 **「위치 · 비전 확인 완료」 줄도 같은 규칙을 탄다**(2026-09-20). 그 체크 마크는
             칸별 판정(`ChamberRow.verdict`)이 있을 때만 그려지는데 서버는 그 축을 주지 않는다
             (BR-31) — live 에서는 **설명할 대상이 화면에 하나도 없다.** 남겨 두면 사용자가
             「체크가 없으니 아직 확인 전이구나」로 읽는데, 사실은 **확인 여부를 우리가 모른다.**
        */}
        <FigureLegend
          items={[
            {
              mark: <LegendBox filled />,
              term: t('monitoring.legend.before'),
              text: preName,
            },
            ...(overview.postTray === true
              ? [{ mark: <LegendBox />, term: t('monitoring.legend.after'), text: t(labels.post) }]
              : []),
            ...(chambers.some((row) => row.verdict != null)
              ? [{ mark: <LegendCheck />, text: t('monitoring.legend.checked') }]
              : []),
          ]}
        />
      </div>
    </div>
  )
}

/**
 * 비전 판정 박스 — 챔버의 검사 판정을 그대로 얹는다. 화면이 판정하지 않는다.
 *
 * 🔴 **판정이 서지 않은 칸은 박스를 그리지 않는다**(2026-09-20 정정). 종전에는 `verdict` 가
 *    `null` 이면 `'pass'` 로 접었는데, 그것은 **「못 봤다」를 「통과」로 적는 것**이라 비전 점검에서
 *    가장 나쁜 쪽으로 틀린다(`PreflightResult.blockers[].verdict` 가 `inconclusive` 를 제3값으로
 *    두는 이유와 같다 — 「`fail` 로 접으면 다시 놓고, 목록에서 빼면 점검하지 않은 채 통과」).
 * 🔴 **`slot` 이 없는 행도 세지 않는다.** 칸 이름이 곧 박스 라벨이라 없으면 그릴 것이 없고,
 *    라벨을 지어내면 **없는 칸의 판정을 발명**하는 것이 된다. live 에서는 그래서 빈 배열이다.
 *    ⚠ **탭이 있다고 박스를 채우지 마라**(eric 지시 2026-09-21 로 탭이 돌아왔다). 돌아온 것은
 *      탭이지 판정이 아니다 — live 에서 이 함수가 `[]` 를 내는 것이 정답이고, 탭 안은
 *      「카메라 영상 연결 없음」 한 줄로 **왜 비었는지**만 말한다.
 */
function visionCells(
  deviceKey: MonitoringDeviceKey,
  chambers: readonly ChamberRow[],
): VisionCellView[] {
  const judged = chambers.filter(
    (row) => row.slot !== undefined && (row.verdict === 'pass' || row.verdict === 'fail'),
  )
  const inside = judged.filter((row) => whereOf(row, deviceKey) === 'inModule')
  const source = inside.length > 0 ? inside : judged
  const limit = deviceKey === 'cent' ? 4 : deviceKey === 'asm' ? 2 : 9
  return source.slice(0, limit).map((row) => ({
    label: row.slot ?? '',
    verdict: row.verdict === 'fail' ? ('fail' as const) : ('pass' as const),
  }))
}

function LegendBox({ filled = false }: { filled?: boolean }) {
  return (
    <svg aria-hidden="true" height={15} viewBox="0 0 16 16" width={15}>
      <rect
        className={filled ? 'fig-mod__solid' : 'fig-mod__well'}
        height={10}
        rx={2}
        width={13}
        x={1.5}
        y={3}
      />
    </svg>
  )
}

function LegendCheck() {
  return (
    <svg aria-hidden="true" height={15} viewBox="0 0 16 16" width={15}>
      <circle className="fig-vm__mark" cx={8} cy={8} r={7} />
      <text className="fig-vm__glyph" textAnchor="middle" x={8} y={11.5}>
        ✓
      </text>
    </svg>
  )
}
